import {
  FilterOutlined,
  HolderOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Empty, message, Select, Space, Table, Typography } from 'antd';
import { FC, HTMLAttributes, useEffect, useMemo, useState } from 'react';

import { useBreadcrumbs } from 'src/features/layout/hooks/useBreadcrumbs';

import { BannerOrderingFooter } from '../components/BannerOrderingFooter';
import { useBannerOrder, useBannerUpdateOrder } from '../hooks/useBanner';
import {
  BannerOrderItem,
  BannerSegment,
  BannerSlot,
  DeviceTypeEnum,
} from '../types/bannerManagement';

const { Title } = Typography;

interface RowProps extends HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': number;
}

const DraggableRow: FC<RowProps> = ({ children, ...props }) => {
  const rowKey = props['data-row-key'];

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rowKey ?? -1 });

  // Empty-state row has no valid row key — render plain <tr> to avoid
  // PointerSensor registering onPointerDown on the full-width placeholder,
  // which would trigger pointer capture and freeze navigation.
  if (!rowKey) {
    return <tr {...props}>{children}</tr>;
  }

  const style = {
    ...props.style,
    transform: transform
      ? CSS.Transform.toString({ ...transform, scaleY: 1 })
      : undefined,
    transition,
    cursor: 'move',
    ...(isDragging ? { position: 'relative' as const, zIndex: 1000 } : {}),
  };

  return (
    <tr
      {...props}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    >
      {children}
    </tr>
  );
};

export const BannerOrderingScreen: FC = () => {
  useBreadcrumbs([
    {
      label: 'Loja',
    },
    {
      label: 'Banners',
    },
    {
      label: 'Ordenação',
    },
  ]);

  const [deviceType, setDeviceType] = useState<DeviceTypeEnum | undefined>(
    undefined
  );
  const [slotType, setSlotType] = useState<BannerSlot | undefined>(undefined);
  const [targetMarket, setTargetMarket] = useState<BannerSegment | undefined>(
    undefined
  );
  const [draftItems, setDraftItems] = useState<BannerOrderItem[]>([]);
  const [savedItems, setSavedItems] = useState<BannerOrderItem[]>([]);
  const [isDirty, setIsDirty] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  const allFiltersSelected = !!deviceType && !!slotType && !!targetMarket;

  const queryParams = useMemo(
    () =>
      allFiltersSelected
        ? ({ deviceType, slotType, targetMarket } as const)
        : null,
    [allFiltersSelected, deviceType, slotType, targetMarket]
  );

  const { orderedBanners, isLoading } = useBannerOrder(queryParams);
  const { updateBannerOrder, isUpdatingBannerOrder } = useBannerUpdateOrder();

  useEffect(() => {
    setDraftItems(orderedBanners);
    setSavedItems(orderedBanners);
    setIsDirty(false);
  }, [orderedBanners]);

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }

    if (isUpdatingBannerOrder) {
      return;
    }

    const oldIndex = draftItems.findIndex(item => item.id === active.id);
    const newIndex = draftItems.findIndex(item => item.id === over.id);

    if (oldIndex < 0 || newIndex < 0) {
      return;
    }

    const reorderedItems = arrayMove(draftItems, oldIndex, newIndex).map(
      (item, index) => ({
        ...item,
        order: index + 1,
      })
    );

    setDraftItems(reorderedItems);
    setIsDirty(true);
  };

  const handleSaveOrder = async () => {
    const payload = {
      banners: draftItems.map((item, index) => ({
        id: item.id,
        order: index + 1,
      })),
    };

    try {
      await updateBannerOrder(payload);
      setSavedItems(draftItems);
      setIsDirty(false);
    } catch {
      message.error('Nao foi possivel atualizar a ordenacao.');
    }
  };

  const handleCancelOrder = () => {
    setDraftItems(savedItems);
    setIsDirty(false);
  };

  return (
    <Space direction='vertical' size={16} style={{ width: '100%' }}>
      <Title level={3}>Ordenacao de Banners</Title>

      <Space wrap>
        <Select
          value={deviceType ?? null}
          placeholder='Dispositivo'
          onChange={value => setDeviceType(value as DeviceTypeEnum)}
          style={{ width: 180 }}
          options={[
            { label: 'Web', value: DeviceTypeEnum.WEB },
            { label: 'App', value: DeviceTypeEnum.MOBILE },
            { label: 'Tablet', value: DeviceTypeEnum.TABLET },
            { label: 'TV', value: DeviceTypeEnum.TV },
          ]}
        />

        <Select
          value={slotType ?? null}
          placeholder='Posição'
          onChange={value => setSlotType(value as BannerSlot)}
          style={{ width: 180 }}
          options={[
            { label: 'Topo', value: BannerSlot.Top },
            { label: 'Meio', value: BannerSlot.Middle },
            { label: 'Fundo', value: BannerSlot.Bottom },
          ]}
        />

        <Select
          value={targetMarket ?? null}
          placeholder='Segmento'
          onChange={value => setTargetMarket(value as BannerSegment)}
          style={{ width: 180 }}
          options={[
            { label: 'Assinante', value: BannerSegment.Subscriber },
            { label: 'Freemium', value: BannerSegment.Freemium },
          ]}
        />
      </Space>

      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <SortableContext
          items={draftItems.map(item => item.id)}
          strategy={verticalListSortingStrategy}
        >
          <Table
            loading={isLoading || isUpdatingBannerOrder}
            rowKey='id'
            pagination={false}
            bordered
            dataSource={draftItems}
            locale={{
              emptyText: allFiltersSelected ? (
                <Empty
                  image={
                    <InboxOutlined style={{ fontSize: 48, color: '#595959' }} />
                  }
                  imageStyle={{ height: 56 }}
                  description='Nenhum banner encontrado para esta combinação de filtros.'
                />
              ) : (
                <Empty
                  image={
                    <FilterOutlined
                      style={{ fontSize: 48, color: '#595959' }}
                    />
                  }
                  imageStyle={{ height: 56 }}
                  description={
                    <Space direction='vertical' size={4}>
                      <span>Selecione os 3 filtros acima</span>
                      <span style={{ fontSize: 12 }}>
                        Dispositivo, Posição e Segmento são necessários para
                        exibir a ordenação.
                      </span>
                    </Space>
                  }
                />
              ),
            }}
            components={{
              body: {
                row: DraggableRow,
              },
            }}
            style={{
              isolation: 'isolate',
            }}
            columns={[
              {
                title: '',
                width: 48,
                render: () => <HolderOutlined />,
              },
              {
                title: 'Ordem',
                width: 32,
                render: (_: unknown, __: BannerOrderItem, index: number) => (
                  <Typography.Text
                    type='secondary'
                    style={{ textAlign: 'center', display: 'block' }}
                  >
                    {index + 1}
                  </Typography.Text>
                ),
              },
              {
                title: 'Titulo',
                dataIndex: 'title',
                render: (title: string) =>
                  title ? (
                    title
                  ) : (
                    <Typography.Text
                      type='secondary'
                      italic
                      style={{ fontSize: 12 }}
                    >
                      Sem título
                    </Typography.Text>
                  ),
              },
            ]}
          />
        </SortableContext>
      </DndContext>
      <BannerOrderingFooter
        isDirty={isDirty}
        isLoading={isUpdatingBannerOrder}
        onSave={handleSaveOrder}
        onCancel={handleCancelOrder}
      />
    </Space>
  );
};
